import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

export function MakeAuthTools<User, Tokens, Credentials>() {
  type AuthContextType = {
    user: User | null
    setUser: (user: User | null) => void;
    tokens: Tokens | null
    performLogin: (credentials: Credentials) => Promise<void>
    performLogout: () => void

    loginError: Error | null
    isLoggingIn: boolean
    refreshError: Error | null
    isRefreshing: boolean

    // loading state and error state for user data fetching
    userDataLoading: boolean
    userDataError: Error | null
  }

  const AuthContext = createContext<AuthContextType>({
    user: null,
    setUser: () => {},
    tokens: null,
    performLogin: async () => {},
    performLogout: () => {},
    loginError: null,
    isLoggingIn: false,
    refreshError: null,
    isRefreshing: false,
    userDataLoading: false,
    userDataError: null,
  })

  function loadTokensFromStorage(): Tokens | null {
    const tokens = localStorage.getItem("tokens")
    if (tokens) {
      return JSON.parse(tokens)
    }
    return null
  }

  function saveTokensToStorage(tokens: Tokens) {
    localStorage.setItem("tokens", JSON.stringify(tokens))
  }

  function removeTokensFromStorage() {
    localStorage.removeItem("tokens")
  }

  type AuthProviderProps = {
    children?: React.ReactNode
    login: (credentials: Credentials) => Promise<Tokens | null>
    refresh: (tokens: Tokens) => Promise<Tokens | null>
    getUserData: (tokens: Tokens) => Promise<User | null>
    getTokenExpireDate: (tokens: Tokens) => Date
    getRefreshExpireDate?: (tokens: Tokens) => Date
  }

  const REFRESH_BEFORE_EXPIRE = 1000 * 60 * 1 // 1 minute

  function AuthProvider({
    children,
    login,
    refresh,
    getUserData,
    getTokenExpireDate,
    getRefreshExpireDate,
  }: AuthProviderProps) {
    const [tokens, setTokens] = useState<Tokens | null>(loadTokensFromStorage())
    const [user, setUser] = useState<User | null>(null)
    const [loaded, setLoaded] = useState(false)
    const isAuthLoadedRef = useRef(false)

    const [loginError, setLoginError] = useState<Error | null>(null)
    const [refreshError] = useState<Error | null>(null)

    const fetchUserData = useCallback(
      async (tokens: Tokens) => {
        const userData = await getUserData(tokens)
        if (userData !== null) {
          setUser(userData)
          return userData
        } else {
          return null
        }
      },
      [getUserData]
    )

    const refreshTokens = useCallback(
      async (currentTokens: Tokens) => {
        const newTokens = await refresh(currentTokens)
        if (newTokens !== null) {
          setTokens(newTokens)
          saveTokensToStorage(newTokens)
          return newTokens
        } else {
          return null
        }
      },
      [refresh]
    )

    useEffect(() => {
      async function bootstrapAuth() {
        try {
          if (!tokens) {
            return
          }
          let currentTokens: Tokens = tokens
          let exp = getTokenExpireDate(currentTokens)
          const now = Date.now()
          let timeToExpire = exp.getTime() - now
          if (timeToExpire < REFRESH_BEFORE_EXPIRE) {
            if (refresh && getRefreshExpireDate) {
              const refreshExp = getRefreshExpireDate(currentTokens)
              const refreshTimeToExpire = refreshExp.getTime() - now
              if (refreshTimeToExpire > 0) {
                const newTokens: Tokens | null = await refresh(currentTokens)
                if (newTokens) {
                  const nextTokens: Tokens = { ...currentTokens, ...newTokens }
                  setTokens(newTokens)
                  saveTokensToStorage(nextTokens)
                  currentTokens = nextTokens
                  exp = getTokenExpireDate(nextTokens)
                  timeToExpire = exp.getTime() - now
                }
              }
            }
          }
          if (timeToExpire > REFRESH_BEFORE_EXPIRE) {
            const user = await fetchUserData(currentTokens)
            if (user) {
              setUser(user)
            }
          }
        } finally {
          setLoaded(true)
        }
      }

      if (!isAuthLoadedRef.current) {
        isAuthLoadedRef.current = true
        bootstrapAuth()
      }
    }, [fetchUserData, getRefreshExpireDate, getTokenExpireDate, refresh, refreshTokens, tokens])

    useEffect(() => {
      if (tokens) {
        const exp = getTokenExpireDate(tokens)
        const now = Date.now()
        const timeToExpire = exp.getTime() - now
        if (timeToExpire > REFRESH_BEFORE_EXPIRE) {
          const t = setTimeout(() => {
            refreshTokens(tokens)
          }, timeToExpire - REFRESH_BEFORE_EXPIRE)
          return () => {
            clearTimeout(t)
          }
        }
      }
    }, [getTokenExpireDate, refreshTokens, tokens])

    const performLogin = useCallback(
      async (credentials: Credentials) => {
        const loginTokens = await login(credentials).catch((e) => {
          setLoginError(e)
          return null
        })
        if (loginTokens !== null) {
          const user = await fetchUserData(loginTokens)
          if (user) {
            setTokens(loginTokens)
            saveTokensToStorage(loginTokens)
            setLoginError(null)
          }
        }
      },
      [fetchUserData, login]
    )

    const performLogout = useCallback(() => {
      removeTokensFromStorage()
      setUser(null)
      setTokens(null)
    }, [])

    const ctx = useMemo(() => {
      return {
        user,
        setUser,
        performLogin,
        performLogout,
        tokens,
        loginError,
        isLoggingIn: false,
        refreshError,
        isRefreshing: false,
        userDataLoading: false,
        userDataError: null,
      }
    }, [performLogin, performLogout, tokens, user, loginError, refreshError])

    if (!loaded) {
      return null
    }

    return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>
  }

  function useAuth() {
    const context = useContext<AuthContextType>(AuthContext as unknown as React.Context<AuthContextType>)
    if (!context) {
      throw new Error("useAuth must be used under AuthProvider")
    }
    return context
  }

  return [AuthProvider, useAuth] as [typeof AuthProvider, typeof useAuth]
}